import React from "react";
import { Link } from "react-router-dom";

const EFFECTIVE_DATE = "May 18, 2026";

export default function PrivacyPage() {
    return (
        <div className="min-h-screen bg-[#0B1220] text-slate-100 px-6 py-16">
            <div className="max-w-3xl mx-auto" data-testid="privacy-page">
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Legal</p>
                <h1 className="font-serif text-4xl sm:text-5xl mb-2">Privacy Policy</h1>
                <p className="text-slate-400 text-sm mb-10">Effective {EFFECTIVE_DATE}</p>

                <Section title="1. Who we are">
                    This Privacy Policy explains how <strong>All Purpose Ventures LLC</strong>{" "}
                    (doing business as “The Butler Room”) collects, uses, and protects your
                    information when you use{" "}
                    <a href="https://thebutlerroom.com" className="text-[#D4AF37] underline">thebutlerroom.com</a>{" "}
                    and related services (“Platform”). Questions? Email{" "}
                    <a href="mailto:info@allpurposeventures.com" className="text-[#D4AF37] underline">info@allpurposeventures.com</a>.
                </Section>

                <Section title="2. Information we collect">
                    <h3 className="font-medium text-white mt-2 mb-1">a) Information you give us</h3>
                    <ul className="list-disc pl-5 space-y-1 text-slate-300">
                        <li>Account details: name, email, password (hashed), phone number.</li>
                        <li>Task details: description, category, service address, preferred date, and photos you upload of the work area.</li>
                        <li>Contractor onboarding (for Partners only): business name, EIN (last 4 digits), service area, signed agreement, electronic signature.</li>
                        <li>Tax forms (for Partners only): IRS Form W-9 data including legal name, federal tax classification, address, and taxpayer identification number (SSN or EIN). The full TIN is encrypted at rest; only the last 4 digits are ever displayed in our dashboards. Used solely to issue your 1099-NEC and meet IRS recordkeeping requirements.</li>
                        <li>Communications: messages you send us via the chatbot, contact forms, or email.</li>
                    </ul>

                    <h3 className="font-medium text-white mt-4 mb-1">b) Information collected automatically</h3>
                    <ul className="list-disc pl-5 space-y-1 text-slate-300">
                        <li>Device & browser info (browser type, OS, IP address, language).</li>
                        <li>Usage data (pages visited, buttons clicked, session length, referring URL).</li>
                        <li>Cookies and similar technologies (see <a href="#cookies" className="text-[#D4AF37] underline">Section 6</a>).</li>
                    </ul>

                    <h3 className="font-medium text-white mt-4 mb-1">c) Information from third parties</h3>
                    <ul className="list-disc pl-5 space-y-1 text-slate-300">
                        <li>Payment metadata from Stripe (e.g., last 4 digits of card, charge ID) — we never store full card numbers.</li>
                        <li>Email delivery status from Resend (sent / opened / bounced).</li>
                    </ul>
                </Section>

                <Section title="3. How we use your information">
                    <ul className="list-disc pl-5 space-y-1 text-slate-300">
                        <li>Match cleaning tasks with suitable Cleaners and operate the platform.</li>
                        <li>Generate AI quote estimates using Google Gemini (your task description and photos are sent to Gemini for analysis).</li>
                        <li>Send transactional emails (account verification, password reset, task confirmations, partner invites, weekly payout statements).</li>
                        <li>Process client payments and weekly Cleaner payouts via Stripe.</li>
                        <li>Improve the Platform, analyze traffic, and prevent fraud.</li>
                        <li>Comply with legal obligations.</li>
                    </ul>
                </Section>

                <Section title="4. AI processing (Google Gemini)">
                    When you submit a task description or upload photos to receive an AI estimate,
                    those inputs are sent to Google’s Gemini API. Per Google’s policies, this data
                    is processed to return a response and is <strong>not used to train Google’s
                    consumer models</strong>. We do not send Google your name, email, phone, or
                    home address. We recommend you do not include sensitive personal information,
                    passwords, or financial data in task descriptions.
                </Section>

                <Section title="5. Who we share information with">
                    We share the minimum information necessary, only with:
                    <ul className="list-disc pl-5 space-y-1 text-slate-300 mt-2">
                        <li><strong>Cleaners</strong> matched to your task — they receive your name, phone, service address, and task details to perform the work.</li>
                        <li><strong>Stripe</strong> — to process client checkouts and weekly Cleaner payouts (PCI-compliant).</li>
                        <li><strong>Resend</strong> — to deliver transactional emails (recipient email + content).</li>
                        <li><strong>Google Gemini</strong> — task descriptions and photos for AI quoting (see Section 4).</li>
                        <li><strong>Google Analytics & Microsoft Clarity</strong> — pseudonymous usage analytics (see Section 6).</li>
                        <li><strong>Emergent Object Storage</strong> — secure storage of uploaded task photos.</li>
                        <li><strong>Law enforcement or regulators</strong> — only when legally required.</li>
                    </ul>
                    <p className="mt-2">We <strong>never sell</strong> your personal information.</p>
                </Section>

                <Section title="6. Cookies & tracking" id="cookies">
                    We use the following on the live site at thebutlerroom.com:
                    <ul className="list-disc pl-5 space-y-1 text-slate-300 mt-2">
                        <li><strong>Strictly necessary cookies</strong> — keep you logged in (session JWT) and remember security preferences. Cannot be disabled.</li>
                        <li><strong>Google Analytics 4</strong> (Measurement ID G-3LWNNR7VMZ) — measures traffic, sources, and conversion events. IP anonymization is enabled.</li>
                        <li><strong>Microsoft Clarity</strong> (Project wt7brh973x) — records anonymized session replays and heatmaps to help us improve UX. We do not see passwords or payment fields (Clarity masks them automatically).</li>
                    </ul>
                    <p className="mt-2">
                        You can disable analytics at any time through our cookie banner, by using
                        your browser’s “Do Not Track” setting, or by installing add-ons like the{" "}
                        <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noreferrer" className="text-[#D4AF37] underline">Google Analytics Opt-Out Add-on</a>.
                    </p>
                </Section>

                <Section title="7. How we protect your information">
                    <ul className="list-disc pl-5 space-y-1 text-slate-300">
                        <li>All traffic is encrypted with HTTPS/TLS.</li>
                        <li>Passwords are hashed with bcrypt — we never store them in plain text.</li>
                        <li>Database access is restricted to authorized personnel only.</li>
                        <li>Payment card data is handled exclusively by Stripe; we never see or store full card numbers.</li>
                    </ul>
                    <p className="mt-2">
                        No system is 100% secure. If we ever discover a data breach affecting your
                        information, we will notify you as required by Florida law.
                    </p>
                </Section>

                <Section title="8. How long we keep your information">
                    <ul className="list-disc pl-5 space-y-1 text-slate-300">
                        <li>Account info: while your account is active, plus 3 years for tax / dispute records.</li>
                        <li>Task records: 3 years from task completion.</li>
                        <li>Partner agreements: 7 years (tax-record retention).</li>
                        <li>Analytics data: up to 26 months in Google Analytics; ~3 months in Microsoft Clarity.</li>
                    </ul>
                </Section>

                <Section title="9. Your rights">
                    Depending on where you live, you may have the right to:
                    <ul className="list-disc pl-5 space-y-1 text-slate-300 mt-2">
                        <li>Access the personal information we hold about you.</li>
                        <li>Correct or update inaccurate information.</li>
                        <li>Request deletion of your account and associated data.</li>
                        <li>Object to or restrict certain processing.</li>
                        <li>Withdraw consent for analytics cookies.</li>
                        <li>Receive a copy of your data in a portable format.</li>
                    </ul>
                    <p className="mt-2">
                        To exercise any of these rights, email{" "}
                        <a href="mailto:info@allpurposeventures.com" className="text-[#D4AF37] underline">info@allpurposeventures.com</a>.
                        We will respond within 30 days. Note: we may need to retain certain
                        information to comply with tax or legal obligations.
                    </p>
                </Section>

                <Section title="10. Children">
                    The Platform is not directed to children under 18. We do not knowingly collect
                    information from anyone under 18. If you believe a minor has provided us
                    information, please contact us so we can delete it.
                </Section>

                <Section title="11. International users">
                    The Butler Room is operated from the United States. If you access the Platform
                    from outside the US, your information will be transferred to and processed in
                    the US, which may have different data-protection laws than your country.
                </Section>

                <Section title="12. Changes to this policy">
                    We may update this Privacy Policy from time to time. The effective date at the
                    top of the page will reflect the most recent revision. For material changes,
                    we will notify registered users by email.
                </Section>

                <Section title="13. Contact">
                    All Purpose Ventures LLC<br />
                    DBA The Butler Room<br />
                    Central Florida<br />
                    Email:{" "}
                    <a href="mailto:info@allpurposeventures.com" className="text-[#D4AF37] underline">
                        info@allpurposeventures.com
                    </a>
                </Section>

                <div className="mt-12 pt-8 border-t border-white/10 text-sm text-slate-400">
                    <Link to="/" className="text-[#D4AF37] hover:underline" data-testid="privacy-back-home">← Back to home</Link>
                    <span className="mx-3 text-slate-600">·</span>
                    <Link to="/terms" className="text-[#D4AF37] hover:underline" data-testid="privacy-to-terms">Terms of Service</Link>
                </div>
            </div>
        </div>
    );
}

function Section({ title, children, id }) {
    return (
        <section className="mb-10" id={id}>
            <h2 className="font-serif text-2xl mb-3 text-white">{title}</h2>
            <div className="text-slate-300 leading-relaxed text-[15px]">{children}</div>
        </section>
    );
}
